"use client";

import { Popover } from "@headlessui/react";
import {
  ChevronDownIcon as ChevronDownOutlineIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon as ChevronDownSolidIcon } from "@heroicons/react/24/solid";
import { useState } from "react";

import { ReportBugDialog } from "@/components/layout/dialogs/ReportBugDialog";
import { UserPreferencesDialog } from "@/components/layout/dialogs/UserPreferencesDialog";
import { SettingsControls } from "@/components/layout/settings/SettingsControls";
import { Button } from "@/components/ui/Button";
import { useLanguage } from "@/context/LanguageContext";

type SettingsMenuProps = {
  switchId: string;
  isMobile?: boolean;
  menuPosition?: "bottom" | "top";
  onAfterAction?: () => void;
};

export function SettingsMenu({
  switchId,
  isMobile = false,
  menuPosition = "bottom",
  onAfterAction,
}: SettingsMenuProps) {
  const { t } = useLanguage();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const navbar = t.navbar;
  function handleBugReportOpen() {
    setIsBugReportOpen(true);
    setIsSettingsOpen(false);
    onAfterAction?.();
  }

  function handlePreferencesOpen() {
    setIsPreferencesOpen(true);
    setIsSettingsOpen(false);
    onAfterAction?.();
  }

  const controls = (
    <SettingsControls
      navbar={navbar}
      switchId={switchId}
      onBugReportOpen={handleBugReportOpen}
      onPreferencesOpen={handlePreferencesOpen}
    />
  );

  if (isMobile) {
    return (
      <>
        <div className="relative w-full space-y-1">
          <Button
            aria-expanded={isSettingsOpen}
            type="button"
            variant="unstyled"
            onClick={() => setIsSettingsOpen((current) => !current)}
            className="group settings-row h-auto justify-start gap-x-3 p-1"
          >
            <div className="settings-icon-wrapper">
              <Cog6ToothIcon aria-hidden="true" className="settings-icon" />
            </div>

            <div className="flex-auto min-w-0">
              <span className="settings-label truncate">{navbar.settings}</span>
            </div>

            <ChevronDownOutlineIcon
              className={`settings-chevron ${
                isSettingsOpen ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            />
          </Button>

          {isSettingsOpen ? (
            <div className="space-y-1 pl-4">{controls}</div>
          ) : null}
        </div>

        <ReportBugDialog
          isOpen={isBugReportOpen}
          onOpenChange={setIsBugReportOpen}
        />
        <UserPreferencesDialog
          isOpen={isPreferencesOpen}
          onOpenChange={setIsPreferencesOpen}
        />
      </>
    );
  }

  return (
    <>
      <Popover className="desktop-nav__settings-menu">
        {({ close }) => (
          <>
            <Popover.Button
              aria-label={navbar.settings}
              className="desktop-nav__settings-btn"
            >
              <Cog6ToothIcon
                className="size-4 shrink-0 text-foreground"
                aria-hidden="true"
              />
              <span className="sr-only">{navbar.settings}</span>
              <ChevronDownSolidIcon
                aria-hidden="true"
                className="desktop-nav__chevron"
              />
            </Popover.Button>
            <Popover.Overlay className="desktop-nav__overlay" />

            <Popover.Panel
              className={
                menuPosition === "top"
                  ? "settings-popover-panel settings-popover-panel--top"
                  : "settings-popover-panel settings-popover-panel--bottom"
              }
            >
              <div className="desktop-nav__panel-inner space-y-1">
                <SettingsControls
                  navbar={navbar}
                  onCloseMenu={close}
                  switchId={switchId}
                  onBugReportOpen={() => {
                    handleBugReportOpen();
                    close();
                  }}
                  onPreferencesOpen={() => {
                    handlePreferencesOpen();
                    close();
                  }}
                />
              </div>
            </Popover.Panel>
          </>
        )}
      </Popover>

      <ReportBugDialog
        isOpen={isBugReportOpen}
        onOpenChange={setIsBugReportOpen}
      />
      <UserPreferencesDialog
        isOpen={isPreferencesOpen}
        onOpenChange={setIsPreferencesOpen}
      />
    </>
  );
}
